// components/Admin/BotHub.tsx
//
// Dedicated entry point for seeding sessions with bots — deliberately NOT linked
// from the live session screen. Pick which quiz gets bots, then which session
// (or spin up a fresh game), and jump into the bot control surface for it.
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { db } from '../../firebase';
import { Quiz } from '../../types/quiz.types';
import { useQuiz } from '../../hooks/useQuiz';
import { useAuth } from '../../context/Auth/auth-context';

interface SessionRow {
  quizId: string;
  status: 'not_started' | 'in_progress' | 'ended';
  createdAt?: number;
}

const statusBadge = (status: string) => {
  switch (status) {
    case 'not_started': return 'bg-accent/30 text-ink';
    case 'in_progress': return 'bg-tile-green text-white';
    case 'ended': return 'bg-ink/10 text-ink/60';
    default: return 'bg-ink/10 text-ink/60';
  }
};

export const BotHub = () => {
  const [quizzes, setQuizzes] = useState<Record<string, Quiz>>({});
  const [sessions, setSessions] = useState<Record<string, SessionRow>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const { createSession, loading } = useQuiz();
  const { user } = useAuth();
  const navigate = useNavigate();

  // One-shot reads — the hub is a launchpad, not a live surface. Holding two
  // always-on listeners over the ENTIRE quizzes + sessions trees re-downloads
  // both blobs on every write anywhere in them; a get() on mount is enough.
  useEffect(() => {
    get(ref(db, 'quizzes')).then((snap) => setQuizzes(snap.val() || {}));
    get(ref(db, 'sessions')).then((snap) => setSessions(snap.val() || {}));
  }, []);

  const sessionsFor = (quizId: string) =>
    Object.entries(sessions)
      .filter(([, s]) => s.quizId === quizId)
      .sort((a, b) => (b[1].createdAt ?? 0) - (a[1].createdAt ?? 0));

  // Create a fresh game purely to run bots against, then drop straight into the
  // bot control surface for it.
  const hostForBots = async (quizId: string) => {
    if (!user) return;
    const code = await createSession(quizId, user);
    if (code) navigate(`/admin/session/${code}/bots`);
  };

  const filtered = Object.entries(quizzes).filter(([, q]) =>
    q.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-extrabold font-display tracking-tight">Bots</h1>
        <p className="text-sm text-slate-500">Choose which quiz gets bots, then a session to run them in.</p>
      </div>

      <input
        type="text"
        placeholder="Search quizzes…"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full p-3 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none mb-5"
      />

      <div className="space-y-3">
        {filtered.length === 0 && (
          <p className="text-slate-400 text-center py-10">No quizzes yet — create one first.</p>
        )}
        {filtered.map(([quizId, quiz]) => {
          const rows = sessionsFor(quizId);
          return (
            <div key={quizId} className="card p-5">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="text-lg font-bold truncate">{quiz.title || 'Untitled quiz'}</h3>
                  <p className="text-sm text-slate-500">
                    {Object.keys(quiz.questions || {}).length} questions · {rows.length} session{rows.length === 1 ? '' : 's'}
                  </p>
                </div>
                <button
                  onClick={() => hostForBots(quizId)}
                  disabled={loading}
                  className="btn-success text-sm shrink-0"
                >
                  {loading ? 'Creating…' : '+ New game for bots'}
                </button>
              </div>

              {rows.length > 0 && (
                <div className="mt-4 space-y-2">
                  {rows.map(([code, s]) => (
                    <div key={code} className="flex items-center gap-3 p-2.5 rounded-xl bg-ink/5">
                      <span className="font-bold font-display tabular-nums tracking-wider">{code}</span>
                      <span className={`chip ${statusBadge(s.status)}`}>
                        {s.status.replace('_', ' ').toUpperCase()}
                      </span>
                      <Link
                        to={`/admin/session/${code}/bots`}
                        className={`ml-auto text-sm font-semibold ${
                          s.status === 'ended' ? 'text-slate-400 pointer-events-none' : 'text-ink underline decoration-2 decoration-accent underline-offset-2'
                        }`}
                      >
                        Run bots →
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
